export const DOCUMENT_UPLOAD_ALLOWED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/markdown",
  "text/plain",
];

export const DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS = [
  ".pdf",
  ".docx",
  ".md",
  ".txt",
];

export const AUDIT_UPLOAD_MAX_BODY_BYTES = 25 * 1024 * 1024;
export const AUDIT_UPLOAD_MAX_FILES = 10;

export const DOCUMENT_UPLOAD_MAX_SIZE_BYTES = 25 * 1024 * 1024; // 25MB

export const DOCUMENT_UPLOAD_ACCEPT = DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS.join(
  ",",
);

export const IMAGE_UPLOAD_ALLOWED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
];

export const IMAGE_UPLOAD_ALLOWED_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
];

export const IMAGE_UPLOAD_MAX_SIZE_BYTES = 25 * 1024 * 1024;

export const IMAGE_UPLOAD_ACCEPT = IMAGE_UPLOAD_ALLOWED_EXTENSIONS.join(",");

export const EVIDENCE_UPLOAD_ACCEPT = [
  DOCUMENT_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_ACCEPT,
].join(",");

export type EvidenceFileKind = "document" | "image";

export function isAllowedDocumentMimeType(mimeType: string): boolean {
  return DOCUMENT_UPLOAD_ALLOWED_MIME_TYPES.includes(
    mimeType.toLowerCase().trim(),
  );
}

export function isAllowedDocumentExtension(filename: string): boolean {
  const extension = filename.toLowerCase().match(/\.[^.]+$/)?.[0];
  return extension
    ? DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS.includes(extension)
    : false;
}

// NOTE: MIME and extension checks are client-side heuristics.
// Always validate file content (magic bytes) server-side for production uploads.
export function isValidDocument(
  file: { name: string; type: string; size: number },
): boolean {
  return (
    isAllowedDocumentMimeType(file.type) &&
    isAllowedDocumentExtension(file.name) &&
    isDocumentWithinSizeLimit(file.size)
  );
}

export function isDocumentWithinSizeLimit(sizeInBytes: number): boolean {
  return sizeInBytes <= DOCUMENT_UPLOAD_MAX_SIZE_BYTES;
}

export function isAllowedImageMimeType(mimeType: string): boolean {
  return IMAGE_UPLOAD_ALLOWED_MIME_TYPES.includes(
    mimeType.toLowerCase().trim(),
  );
}

export function isAllowedImageExtension(filename: string): boolean {
  const extension = filename.toLowerCase().match(/\.[^.]+$/)?.[0];
  return extension
    ? IMAGE_UPLOAD_ALLOWED_EXTENSIONS.includes(extension)
    : false;
}

export function isImageWithinSizeLimit(sizeInBytes: number): boolean {
  return sizeInBytes <= IMAGE_UPLOAD_MAX_SIZE_BYTES;
}

export function isValidImage(
  file: { name: string; type: string; size: number },
): boolean {
  return (
    isAllowedImageMimeType(file.type) &&
    isAllowedImageExtension(file.name) &&
    isImageWithinSizeLimit(file.size)
  );
}

export type EvidenceFileValidation =
  | { ok: true; kind: EvidenceFileKind }
  | { ok: false; reason: "unsupported_type" | "too_large" };

export function isValidEvidenceFile(
  file: { name: string; type: string; size: number },
): EvidenceFileValidation {
  const isDocType = isAllowedDocumentMimeType(file.type) &&
    isAllowedDocumentExtension(file.name);
  if (isDocType) {
    return isDocumentWithinSizeLimit(file.size)
      ? { ok: true, kind: "document" }
      : { ok: false, reason: "too_large" };
  }

  const isImgType = isAllowedImageMimeType(file.type) &&
    isAllowedImageExtension(file.name);
  if (isImgType) {
    return isImageWithinSizeLimit(file.size)
      ? { ok: true, kind: "image" }
      : { ok: false, reason: "too_large" };
  }

  return { ok: false, reason: "unsupported_type" };
}
